import { useEffect, useRef, useState } from "react";
import type {
  EscalationGenerationCandidate,
  EscalationGenerationInput,
} from "@ai-crm/shared";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

// A monotonic local revision also detects changes that were later undone.
export function useGenerationCandidate<T>(inputSignature: string) {
  const version = useRef({ signature: inputSignature, revision: 0 });
  if (version.current.signature !== inputSignature)
    version.current = {
      signature: inputSignature,
      revision: version.current.revision + 1,
    };
  const requestSequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const [candidate, setCandidate] = useState<{
    value: T;
    revision: number;
  } | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(
    () => () => {
      requestSequence.current += 1;
      controller.current?.abort();
    },
    [],
  );
  async function generate(operation: (signal: AbortSignal) => Promise<T>) {
    const sequence = ++requestSequence.current;
    const revision = version.current.revision;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const timeout = setTimeout(() => abort.abort(), 120_000);
    setPending(true);
    setError(null);
    try {
      const value = await operation(abort.signal);
      if (sequence === requestSequence.current && !abort.signal.aborted)
        setCandidate({ value, revision });
    } catch (failure) {
      if (sequence === requestSequence.current)
        setError(
          abort.signal.aborted
            ? "Запрос прерван. Можно повторить генерацию."
            : failure instanceof Error
              ? failure.message
              : "Не удалось сгенерировать",
        );
    } finally {
      clearTimeout(timeout);
      if (sequence === requestSequence.current) setPending(false);
    }
  }
  return {
    candidate: candidate?.value ?? null,
    stale:
      candidate !== null && candidate.revision !== version.current.revision,
    pending,
    error,
    generate,
    clear: () => setCandidate(null),
  };
}
export function GenerationRemarks({
  warnings,
  missingInformation,
}: {
  warnings: string[];
  missingInformation: string[];
}) {
  return (
    <>
      {warnings.length > 0 && (
        <div role="status">
          <strong>Предупреждения</strong>
          <ul>
            {warnings.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ul>
        </div>
      )}
      {missingInformation.length > 0 && (
        <div>
          <strong>Что стоит уточнить</strong>
          <ul>
            {missingInformation.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
export interface EscalationPromptEditorProps {
  systemPrompt: string;
  handoffConditions: string;
  value: string | null;
  onChange: (value: string | null) => void;
  generate: (
    input: EscalationGenerationInput,
    signal?: AbortSignal,
  ) => Promise<EscalationGenerationCandidate>;
  disabled?: boolean;
  // Conditions are trusted only for the matching current block; null means unknown.
  generatedConditions?: string | null;
}
export function EscalationPromptEditor({
  systemPrompt,
  handoffConditions,
  value,
  onChange,
  generate,
  disabled = false,
  generatedConditions = null,
}: EscalationPromptEditorProps) {
  const generation = useGenerationCandidate<EscalationGenerationCandidate>(
    JSON.stringify([systemPrompt, handoffConditions, value]),
  );
  const [applied, setApplied] = useState<{
    block: string;
    conditions: string;
  } | null>(null);
  const appliedMatches = applied !== null && applied.block === value;
  useEffect(() => {
    if (!appliedMatches) setApplied(null);
  }, [appliedMatches]);
  const baseline = appliedMatches ? applied.conditions : generatedConditions;
  const conditionsChanged =
    value !== null && (baseline === null || baseline !== handoffConditions);
  return (
    <section
      className="space-y-3 rounded border p-3"
      aria-label="Блок передачи менеджеру"
    >
      <Label htmlFor="escalation-prompt">Блок условий передачи менеджеру</Label>
      <Textarea
        id="escalation-prompt"
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value || null)}
        maxLength={10000}
        rows={5}
        disabled={disabled}
      />
      {conditionsChanged && (
        <p role="status">Условия изменились — обновите блок</p>
      )}
      <Button
        type="button"
        variant="outline"
        disabled={disabled || !systemPrompt.trim()}
        onClick={() =>
          generation.generate((signal) =>
            generate({ systemPrompt, handoffConditions }, signal),
          )
        }
      >
        {generation.pending ? "Сгенерировать заново" : "Сгенерировать блок"}
      </Button>
      {generation.error && <p role="alert">{generation.error}</p>}
      {generation.candidate && (
        <div className="space-y-2 rounded border p-3">
          <p>Предложенный блок</p>
          <pre className="whitespace-pre-wrap">
            {generation.candidate.escalationPrompt}
          </pre>
          <GenerationRemarks {...generation.candidate} />
          {generation.stale && (
            <p role="alert">
              Результат устарел: исходный текст или условия изменились.
              Сгенерируйте заново.
            </p>
          )}
          <Button
            type="button"
            disabled={disabled || generation.stale || generation.pending}
            onClick={() => {
              if (!generation.candidate || generation.stale) return;
              onChange(generation.candidate.escalationPrompt);
              setApplied({
                block: generation.candidate.escalationPrompt,
                conditions: handoffConditions,
              });
              generation.clear();
            }}
          >
            Применить блок
          </Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Блок задаёт поведение в разговоре. Автоматический перенос сделки в CRM
        здесь не настраивается.
      </p>
    </section>
  );
}
